'use client';

import React from 'react';

interface BlogPost {
  title: string;
  date: string;
  content: string;
  author: string;
}

const blogPosts: BlogPost[] = [
  {
    title: "UCI Tour of Guangxi Recap and Looking Forwards",
    date: "3 November 2025",
    author: "Sandy",
    content: `Last month, WiderTalk launched with our first live event: the **UCI Tour of Guangxi**. As our inaugural race, it was both a test of our platform and a learning experience that will shape our path forward. Thanks to everyone that watched and commentated!

Here's what happened, what we learned, and where we're heading next.

![Tour of Guangxi Screenshot](https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/guangxi_screenshot_1.png)

**What Went Right**

The **platform works**. Over **six days**, we streamed **20+ hours** of live cycling to a global audience with barely a hiccup. Our infrastructure proved robust, scalable, and ready for 1000s more users.

Our **peak day (Stage 5)** delivered exactly what we set out to create: **250+ unique visitors** tuning in to choose between **three parallel commentary teams** broadcasting from the **UK, Canada, and Germany**. This is the **democratized commentary experience** we envisioned- multiple voices, multiple perspectives, viewer choice. Check out the replay of either [NairoInGreen](https://www.widertalk.com/playback?recording=arn%3Aaws%3Aivs%3Aeu-west-1%3A360045372654%3Acomposition%2FAI6MZPi0vtQT), [Insider Cycling World](https://www.widertalk.com/playback?recording=arn%3Aaws%3Aivs%3Aeu-west-1%3A360045372654%3Acomposition%2FGjvQ9zi5LAVA), or [me and my mate Chris](https://www.widertalk.com/playback?recording=arn%3Aaws%3Aivs%3Aeu-west-1%3A360045372654%3Acomposition%2FsKdumBZZWTPb)!

On **Stage 2** we had a current cycling pro **Bjorn Koerdt** from **Picnic Post NL**, who provided some [great insight](https://www.widertalk.com/playback?recording=arn%3Aaws%3Aivs%3Aeu-west-1%3A360045372654%3Acomposition%2FTvMYx4sesy7U).

![Bjorn Koerdt Commentator Announcement](https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/Bjoern_Koerdt_Commentator_Announcement.jpg)

The feedback from those who participated was **overwhelmingly positive**. Commentators loved the experience, and viewers praised the **chat interaction**.

Behind the scenes, we validated **two critical assumptions**. First, **acquiring broadcasting rights** for mid-tier races is achievable. This isn't an insurmountable barrier. Second, our **cloud infrastructure costs** remained under control.

Perhaps most importantly, we proved this can be built. WiderTalk was created by **5 people in evenings and weekends, self-funded, with zero marketing budget**. If we can deliver this experience at this scale with these constraints, we're optimistic about what's possible as we grow.

![Tour of Guangxi Multiple Commentators](https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/guangxi_screenshot_3.png)

**What We Learned**

**Timing is everything**. Despite being a UCI WorldTour race, the Tour of Guangxi didn't draw the audience we expected. Stages finishing at 7:30 AM GMT meant most cycling fans were still asleep. Add in end-of-season racing quality, and we learned that not all WorldTour races are created equal. The good news is there's a clear appetite for alternative commentary, as we've seen other non-mainstream races pull tens of thousands of viewers when the timing and quality align.

**Mobile-first**. **63% of our traffic** came from mobile devices, with iPhones alone accounting for 45% of all visitors. Our desktop-focused development approach needs to shift.

**Chat with Video**. Users want to see that chat with their video in full screen, as you can do in Youtube Video.

**Platform strategy matters**. **Twitter, YouTube, and Bluesky** drove more traffic than Instagram. We should be focusing our community-building efforts where our audience actually lives.

**Looking Forward**

The Tour of Guangxi taught us that technical execution is just the beginning. We've proven WiderTalk can deliver multi-commentary streaming at scale. Now we need to apply these lessons:

**Better event selection**: Popular events at viewer-friendly times

**Mobile optimization**: Building for how people actually watch

**Sustainable commentator economics**: Developing models that fairly compensate commentators

**Smarter marketing**: Doubling down on Twitter, YouTube, and Bluesky

Thank you to everyone who watched, commentated, and provided feedback during our launch. **New events are coming soon** so get your mic ready!

Want to be part of the next event? Sign up at [WiderTalk](https://www.widertalk.com)`
  },
  {
    title: "Tour of Guangxi - Our First Big Event",
    date: "31 July 2025",
    author: "Sandy",
    content: `Dear Cycling fans,

You'll be delighted to hear that we've secured worldwide rights (excluding China and Macao) for the Tour of Guangxi from the 14th to the 19th October. This includes both the five-day men's stage race and the women's one-day classic. It's a major breakthrough for WiderTalk, proving that exciting content is still available outside the major broadcasters.

The Tour of Guangxi is one of only two UCI WorldTour races in Asia. Last year, Belgium's Lennert Van Eetvelt claimed victory ahead of recent Tour de France sensation Oscar Onley. The women's race was won by Sandra Alonso of the CERATIZIT-WNT Pro Cycling Team. This year promises to be even more competitive, as riders battle for crucial UCI points to avoid relegation. According to ProCyclingStats, confirmed riders include French megastars Paul Seixas and Paul Magnier.

We're working hard at WiderTalk to ensure both the product and the community are ready for the event. We'll launch with what we believe is the minimum viable set of features to make the experience enjoyable. This includes exciting tools like inviting your friends to join your commentary, and real-time chat between commentators and viewers. We're also building a prize pot for the top commentators of the week.

We can't wait for you to join WiderTalk in this experiment to disrupt the world of broadcasting and change the way we watch and interact with sport.

📸 Follow us on Instagram: @widertalk for the latest updates and commentator announcements.

Sandy`
  },
  {
    title: "Origin Of WiderTalk",
    date: "18 July 2025",
    author: "Sandy",
    content: `The idea for Widertalk formed in my brain late December 2023. I was sitting in the lounge of my wife's grandparents in rural Romania, feeling rather sluggish and hungover from too much pork and cognac, watching a Liverpool game with the family. The volume was high and the two commentators' voices bounced around the room.

My wife walked out the room announcing, "I cannot stand these commentators! They talk such nonsense." While I can speak some Romanian, I struggled to understand what the commentators were actually talking about, and wished for a friendly English voice to explain what was happening. Thankfully my nephew-in-law, and aspiring commentator himself, stepped in and provided his own analysis and opinion in excellent English. A few minutes later my father-in-law voiced his disagreement with the commentator's assessment on one of the Liverpool defenders.

This sub-optimal experience got me thinking. In a world of highly tailored, user-generated content, why do we not have our choice of commentators by default? Why could each viewer not select the audio that they would like to hear during the game, depending on language, style, or content? And why should a talented commentator such as my nephew-in-law not be given a platform?

I thought about it in the context of my primary sport, cycling. I enjoy the Tour De France for the competitive racing, however this isn't important for many people. The second most popular reason for watching the Tour De France in the UK is "I enjoy images of the countryside and cultural coverage of the event."* Why should those viewers have a geography expert, a travel guru, or a food presenter as a commentator?

For the next 8 months I spoke to a quite lot of people about the idea, did a fair amount of research on competitors and the industry, and found 3 big problems (who needs 1) I wanted to tackle:

1. Atypical Viewers' Interests are Ignored by Average Sports Commentary.

2. Sports Commentary is one of the Main Areas of Complaint from Audiences (Over 50% of TnTSport's 1 star reviews refer to poor commentary**).

3. There are Too Many Barriers for Aspiring Commentators to Reach Audiences.

I believe that all three of these problems can be tackled by building a community and a platform that will fundamentally change the way sports broadcasters operate. It will enable users to

1. commentate over the event they want, with the friends they want, in the style they want, and earn cash as they build an audience.

2. watch the event with commentators they want, In the language they want, and interact in the way they want.

And that is the core of Widertalk! The team and I have been working to make this idea a reality for the last 9 months, with a VERY BIG launch event planned in October. This will be announced shortly.

In future posts I will detail out progress on the product build, future events, marketing, and anything else that I think might be of interest.

Any feedback or enquiries please reach out using the contact for or leave a comment below.

All the best,

Sandy

*https://business.yougov.com/content/47150-who-is-watching-the-tour-de-france-and-why

**https://uk.trustpilot.com/review/tntsports.co.uk?search=commentary`
  }
];

export default function BlogPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--cream)' }}>
      {/* Hero Section */}
      <div className="text-white py-20" style={{
        background: 'linear-gradient(to bottom right, var(--navy-blue), var(--lightSecondary))'
      }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 gendy-font text-white">
            WiderTalk Blog
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--cream)' }}>
            Insights, updates, and stories from the WiderTalk journey
          </p>
        </div>
      </div>

      {/* Blog Posts */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="space-y-16">
          {blogPosts.map((post, index) => (
            <article key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
              {/* Post Header */}
              <header className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{
                    backgroundColor: 'var(--primary)',
                    color: 'var(--navy-blue)'
                  }}>
                    {post.date}
                  </span>
                  <span className="text-sm" style={{ color: 'var(--pink)' }}>
                    By {post.author}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold gendy-font mb-4" style={{ color: 'var(--navy-blue)' }}>
                  {post.title}
                </h2>
              </header>

              {/* Post Content */}
              <div className="prose prose-lg max-w-none">
                {post.content.split('\n\n').map((paragraph, pIndex) => {
                  // Handle images
                  if (paragraph.startsWith('![')) {
                    const imageMatch = paragraph.match(/!\[(.*?)\]\((.*?)\)/);
                    if (imageMatch) {
                      const [, altText, src] = imageMatch;
                      return (
                        <div key={pIndex} className="my-8">
                          <img
                            src={src}
                            alt={altText}
                            className="w-full rounded-lg shadow-lg"
                            style={{ maxHeight: '500px', objectFit: 'contain' }}
                          />
                        </div>
                      );
                    }
                  }

                  // Handle section titles (standalone bold text that forms a heading)
                  if (paragraph.match(/^\*\*[^*]+\*\*$/) && paragraph.length < 50) {
                    const titleMatch = paragraph.match(/^\*\*([^*]+)\*\*$/);
                    if (titleMatch) {
                      const [, titleText] = titleMatch;
                      return (
                        <h3 key={pIndex} className="text-2xl font-bold mb-4 mt-8" style={{ color: 'var(--navy-blue)' }}>
                          {titleText}
                        </h3>
                      );
                    }
                  }

                  // Handle numbered lists
                  if (paragraph.match(/^\d+\./)) {
                    return (
                      <div key={pIndex} className="my-4">
                        <p className="text-gray-700 leading-relaxed font-medium">
                          {paragraph}
                        </p>
                      </div>
                    );
                  }

                  // Handle links and special formatting
                  if (paragraph.includes('👉') || paragraph.includes('📸')) {
                    return (
                      <div key={pIndex} className="my-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--cream)' }}>
                        <p className="text-gray-700 leading-relaxed font-medium">
                          {paragraph}
                        </p>
                      </div>
                    );
                  }

                  // Handle references (links at the end) - but not bold formatting
                  if ((paragraph.startsWith('*') && !paragraph.startsWith('**')) || (paragraph.startsWith('**') && paragraph.includes('http'))) {
                    return (
                      <div key={pIndex} className="my-4 text-sm text-gray-600 border-l-4 pl-4" style={{ borderColor: 'var(--pink)' }}>
                        <p>{paragraph}</p>
                      </div>
                    );
                  }

                  // Regular paragraphs
                  return (
                    <p key={pIndex} className="text-gray-700 leading-relaxed mb-6">
                      {paragraph.split(/(\[.*?\]\(https?:\/\/[^\s\)]+\)|\*\*[^*]+\*\*|\bhttps?:\/\/[^\s]+)/g).map((part, partIndex) => {
                        // Handle markdown-style links [text](url)
                        const markdownLinkMatch = part.match(/^\[(.*?)\]\((https?:\/\/[^\s\)]+)\)$/);
                        if (markdownLinkMatch) {
                          const [, linkText, url] = markdownLinkMatch;
                          return (
                            <a
                              key={partIndex}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              {linkText}
                            </a>
                          );
                        }

                        // Handle bold formatting **text**
                        const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
                        if (boldMatch) {
                          const [, boldText] = boldMatch;
                          return (
                            <strong key={partIndex} className="font-bold text-gray-800">
                              {boldText}
                            </strong>
                          );
                        }

                        // Handle plain URLs
                        if (part.match(/^https?:\/\//)) {
                          return (
                            <a
                              key={partIndex}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline break-all"
                            >
                              {part}
                            </a>
                          );
                        }
                        return part;
                      })}
                    </p>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
